import React from 'react';
import { AppBar, Toolbar, Box, TextField, InputAdornment, IconButton, Alert, Typography, Button, Popover, List, ListItem, ListItemText, Badge, Dialog, DialogTitle, DialogContent, Chip, Stack, Divider, Avatar } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockClockIcon from '@mui/icons-material/LockClock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


const sidebarWidths = { xs: 64, sm: 180, md: 220, lg: 260 };
const HeaderNavbar = ({ search, onSearchChange, lowStockProducts, onLowStockViewAll, user, showSearch, lowStockDialogOpen, setLowStockDialogOpen, currentSession, onOpenSession, onCloseSession }) => {
  const hasLowStock = Array.isArray(lowStockProducts) && lowStockProducts.length > 0;
  // Use parent dialog state if provided, else local
  const [localDialogOpen, setLocalDialogOpen] = React.useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState(null);
  // Notifications state
  const [notifications, setNotifications] = React.useState([]);
  // Infinite scroll notifications
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [allLoaded, setAllLoaded] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 50;
  React.useEffect(() => {
    setNotifications([]);
    setPage(1);
    setAllLoaded(false);
  }, [user?.token]);
  React.useEffect(() => {
    if (!user?.token || allLoaded) return;
    const fetchNotifications = async () => {
      setLoadingMore(true);
      try {
        const res = await fetch('/api/notifications', {
          headers: { Authorization: user?.token ? `Bearer ${user.token}` : undefined },
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          if (data.length < PAGE_SIZE) setAllLoaded(true);
          setNotifications(prev => [...prev, ...data.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(n => ({ id: n._id, text: n.message }))]);
        }
      } catch {
        setNotifications([]);
      }
      setLoadingMore(false);
    };
    fetchNotifications();
  }, [user?.token, page, allLoaded]);
  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };
  const notificationsOpen = Boolean(notificationsAnchorEl);
  const handleViewAll = () => {
    if (onLowStockViewAll && setLowStockDialogOpen) setLowStockDialogOpen(true);
    else setLocalDialogOpen(true);
  };
  
  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: {
            xs: `${sidebarWidths.xs}px`,
            sm: `${sidebarWidths.sm}px`,
            md: `${sidebarWidths.md}px`,
            lg: `${sidebarWidths.lg}px`,
          },
          width: {
            xs: `calc(100% - ${sidebarWidths.xs}px)` ,
            sm: `calc(100% - ${sidebarWidths.sm}px)` ,
            md: `calc(100% - ${sidebarWidths.md}px)` ,
            lg: `calc(100% - ${sidebarWidths.lg}px)` ,
          },
          transition: 'left 0.2s, width 0.2s',
        }}
      >
        <Toolbar sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, sm: 3 },
          gap: 2
        }}>
          {/* Left: Session Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {currentSession ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                px: 2,
                py: 1,
                bgcolor: '#f0fdf4',
                borderRadius: 2,
                border: '1px solid #bbf7d0'
              }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: '#22c55e',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  }
                }} />
                <Stack spacing={0}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#15803d', fontWeight: 600, lineHeight: 1 }}>
                    ACTIVE SESSION
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#166534', lineHeight: 1.2 }}>
                    {new Date(currentSession.openedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  onClick={onCloseSession}
                  sx={{ 
                    ml: 1,
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: '#dc2626',
                    borderColor: '#fecaca',
                    bgcolor: '#fef2f2',
                    '&:hover': {
                      bgcolor: '#fee2e2',
                      borderColor: '#fca5a5'
                    }
                  }}
                  variant="outlined"
                  startIcon={<LockClockIcon sx={{ fontSize: 16 }} />}
                >
                  Close
                </Button>
              </Box>
            ) : (
              <Button
                size="small"
                onClick={onOpenSession}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  color: '#d97706',
                  borderColor: '#fed7aa',
                  bgcolor: '#fffbeb',
                  '&:hover': {
                    bgcolor: '#fef3c7',
                    borderColor: '#fbbf24'
                  }
                }}
              >
                Open Session
              </Button>
            )}
            
            {/* Low Stock Alert */}
            {hasLowStock && (
              <Box
                onClick={handleViewAll}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: '#fef3c7',
                  borderRadius: 2,
                  border: '1px solid #fbbf24',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: '#fde68a'
                  }
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 18, color: '#d97706' }} />
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>
                  {lowStockProducts.length} Low Stock
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Right: User Info & Notifications */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Notifications */}
            <IconButton 
              onClick={handleNotificationsClick}
              sx={{ 
                bgcolor: notifications.length > 0 ? '#fef2f2' : 'transparent',
                '&:hover': { bgcolor: '#fee2e2' }
              }}
            >
              <Badge 
                badgeContent={notifications.length} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    height: 18,
                    minWidth: 18,
                    padding: '0 4px'
                  }
                }}
              >
                <NotificationsIcon sx={{ fontSize: 22, color: '#64748b' }} />
              </Badge>
            </IconButton>
            
            {/* User Avatar & Info */}
            {user?.user?.username && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                px: 2,
                py: 0.75,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: '#1976d2',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}
                >
                  {user.user.username.charAt(0).toUpperCase()}
                </Avatar>
                <Stack spacing={0}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                    {user.user.username}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'capitalize', lineHeight: 1 }}>
                    {user.user.role}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Popover */}
      <Popover
        open={notificationsOpen}
        anchorEl={notificationsAnchorEl}
        onClose={handleNotificationsClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ 
          sx: { 
            mt: 1.5, 
            width: 360,
            maxWidth: '90vw',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            borderRadius: 2,
            border: '1px solid #e5e7eb'
          } 
        }}
      >
        <Box sx={{ 
          p: 2, 
          pb: 1.5,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
              Notifications
            </Typography>
            <Chip 
              label={notifications.length} 
              size="small" 
              sx={{ 
                bgcolor: '#fee2e2', 
                color: '#dc2626',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 20
              }} 
            />
          </Stack>
        </Box>
        <List 
          sx={{ 
            maxHeight: 360, 
            overflowY: 'auto',
            p: 0,
            '&::-webkit-scrollbar': {
              width: 6
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: '#f1f5f9'
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#cbd5e1',
              borderRadius: 3
            }
          }}
          onScroll={e => {
            const el = e.target;
            if (el.scrollHeight - el.scrollTop === el.clientHeight && !loadingMore && !allLoaded) {
              setPage(p => p + 1);
            }
          }}
        >
          {notifications.length === 0 ? (
            <ListItem sx={{ py: 4, justifyContent: 'center' }}>
              <Stack alignItems="center" spacing={1}>
                <NotificationsIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
                <Typography variant="body2" color="text.secondary">
                  No notifications
                </Typography>
              </Stack>
            </ListItem>
          ) : notifications.map(n => (
            <ListItem 
              key={n.id}
              button
              onClick={async () => {
                try {
                  await fetch(`/api/notifications/${n.id}/read`, {
                    method: 'POST',
                    headers: { Authorization: user?.token ? `Bearer ${user.token}` : undefined },
                  });
                } catch {}
                setNotifications(prev => prev.filter((notif, idx) => notif.id !== n.id || idx < (page-1)*PAGE_SIZE || idx >= page*PAGE_SIZE));
              }}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: '1px solid #f1f5f9',
                '&:hover': {
                  bgcolor: '#f8fafc'
                },
                '&:last-child': {
                  borderBottom: 'none'
                }
              }}
            >
              <ListItemText 
                primary={n.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  color: '#1e293b'
                }}
              />
            </ListItem>
          ))}
          {loadingMore && (
            <ListItem sx={{ py: 2, justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Loading more...
              </Typography>
            </ListItem>
          )}
        </List>
        <Box sx={{ 
          p: 1.5, 
          bgcolor: '#f8fafc',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <Button 
            size="small" 
            onClick={handleNotificationsClose}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              color: '#64748b',
              '&:hover': {
                bgcolor: '#e2e8f0'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </Popover>
      
      {/* Low Stock Dialog */}
      {(!onLowStockViewAll && hasLowStock) && (
        <Dialog open={localDialogOpen} onClose={() => setLocalDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <InventoryIcon color="warning" />
              <Typography variant="h6" fontWeight={700}>Low Stock Alert</Typography>
              <Chip label={`${lowStockProducts.length} items`} size="small" color="warning" />
            </Stack>
          </DialogTitle>
          <DialogContent>
            <List sx={{ pt: 0 }}>
              {lowStockProducts.map((p, idx) => (
                <React.Fragment key={p._id || p.name}>
                  <ListItem>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" fontWeight={600}>
                          {p.name}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Stock: <strong style={{ color: p.stock === 0 ? '#d32f2f' : '#ed6c02' }}>{p.stock}</strong>
                          </Typography>
                          {p.brand && (
                            <Typography variant="body2" color="text.secondary">
                              Brand: {p.brand}
                            </Typography>
                          )}
                          {p.price && (
                            <Typography variant="body2" color="text.secondary">
                              Price: ${p.price.toFixed(2)}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                  {idx < lowStockProducts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default HeaderNavbar;
